import useTranslation from 'next-translate/useTranslation'
import { useState } from 'react'
import {
  PaperAirplaneIcon,
  BoltIcon,
  BriefcaseIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline'
import PricingTable from '../organisms/Pricing/PricingTable'
import InfiniteCarousel from '../molecules/InfiniteCarousel'
import Testimonial from '../molecules/Testimonial'
import ContactForm from '../organisms/ContactForm'
// importing stuff like a caveman, because Next.js is asinine when it comes to local images
import accentureLogo from '../../public/next-images/clients/accenture-logo.png'
import capgeminiLogo from '../../public/next-images/clients/capgemini-logo.png'
import globallogicLogo from '../../public/next-images/clients/global-logic-logo.png'
import nokiaLogo from '../../public/next-images/clients/nokia-logo.png'
import siiLogo from '../../public/next-images/clients/sii-logo.png'
import aliorLogo from '../../public/next-images/clients/alior-logo.png'
import dataartLogo from '../../public/next-images/clients/dataart-logo.png'
import luxoftLogo from '../../public/next-images/clients/luxoft-logo.png'
import onweloLogo from '../../public/next-images/clients/onwelo-logo.png'
import snowflakeLogo from '../../public/next-images/clients/snowflake-logo.png'
import AtsIntegrationTable from '../organisms/Pricing/AtsIntegrationTable'
import EmployerBrandingTable from '../organisms/Pricing/EmployerBrandingTable'

const PricingTemplate = () => {
  const { t } = useTranslation('pricing')

  const [sent, setSent] = useState(false)
  const [activeView, setActiveView] = useState('job')
  const images = [
    accentureLogo,
    capgeminiLogo,
    globallogicLogo,
    nokiaLogo,
    siiLogo,
    aliorLogo,
    dataartLogo,
    luxoftLogo,
    onweloLogo,
    snowflakeLogo,
  ]

  const getButtonClasses = (
    view: string
  ) => `flex items-center justify-center px-6 py-3 text-base font-semibold rounded-full transition-all duration-300 ease-in-out w-full md:w-auto
      ${
        activeView === view
          ? 'bg-primary text-white shadow-lg'
          : 'bg-white text-gray-700 hover:bg-gray-50 shadow-sm'
      }`

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.5s ease-in-out;
        }
      `}</style>

      <section className='bg-gray-bg py-16'>
        <div className='container'>
          <h1 className='text-center text-5xl tracking-tight mt-8 mb-4'>
            {t('title')}
          </h1>
          <p className='text-center text-2xl font-light text-gray-600 max-w-2xl mx-auto'>
            {t('subtitle')}
          </p>
          <div className='flex gap-3 justify-center items-center my-10 flex-wrap'>
            <button
              type='button'
              onClick={() => setActiveView('job')}
              className={getButtonClasses('job')}
            >
              <BriefcaseIcon className='w-5 h-5 mr-2' />
              {t('navJob')}
            </button>
            <button
              type='button'
              onClick={() => setActiveView('ats')}
              className={getButtonClasses('ats')}
            >
              <BoltIcon className='w-5 h-5 mr-2' />
              {t('navAts')}
            </button>
            <button
              type='button'
              onClick={() => setActiveView('eb')}
              className={getButtonClasses('eb')}
            >
              <CheckBadgeIcon className='w-5 h-5 mr-2' />
              {t('navEb')}
            </button>
          </div>
          <div key={activeView} className='animate-fade-in'>
            {activeView === 'job' && <PricingTable />}
            {activeView === 'ats' && <AtsIntegrationTable />}
            {activeView === 'eb' && <EmployerBrandingTable />}
          </div>
        </div>
      </section>
      <section className='bg-white py-16'>
        <div className='container'>
          <h2 className='text-center mb-10'>{t('trustedBy')}</h2>
          <InfiniteCarousel images={images} />
          <div className='flex justify-center mt-10 gap-8 flex-col md:flex-row'>
            <Testimonial
              name='Joanna Wojdan-Liszewska'
              position='Employer Branding Expert'
              content={t('testimonial1')}
              avatar='/next-images/clients/joanna-liszewska-bosch.webp'
              logo='/next-images/clients/bosch-logo.webp'
              logoWidth={364}
              logoHeight={128}
            />
            <Testimonial
              name='Aleksandra Laskus'
              position='Senior HR Business Partner'
              content={t('testimonial2')}
              avatar='/next-images/clients/aleksandra-laskus-stepstone.webp'
              logo='/next-images/clients/stepstone-logo.webp'
              logoWidth={350}
              logoHeight={119}
            />
            <Testimonial
              name='Malwina Kulnicz'
              position='Senior IT Recruitment and EB Specialist '
              content={t('testimonial3')}
              avatar='/next-images/clients/malwina-kulinicz-wp.webp'
              logo='/next-images/clients/wp-logo.webp'
              logoWidth={238}
              logoHeight={148}
            />
          </div>
        </div>
      </section>
      <section className='bg-action py-16'>
        <div className='container'>
          <ContactForm
            onSuccess={() => {
              setSent(true)
            }}
          />
        </div>
      </section>
      {sent && (
        <div className='fixed inset-0 bg-black/50 z-60 flex justify-center items-center p-4'>
          <div className='bg-white rounded-2xl w-full max-w-md py-12 px-8 flex flex-col justify-center items-center shadow-2xl'>
            <PaperAirplaneIcon className='w-24 animate-bounce text-purple' />
            <p className='my-6 font-light text-2xl text-center'>
              Wiadomość wysłana
            </p>
            <button
              type='button'
              className='bg-primary text-white px-6 py-4 rounded-full inline-block text-center text-lg font-medium duration-300 hover:bg-primary-hover'
              onClick={() => setSent(false)}
            >
              {t('common:close')}
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default PricingTemplate
